'use client'
import React, { useEffect, useState } from 'react'
import Modal from '@/ui/modal'
import Button from '@/ui/button';
import Input from '@/ui/input';
import Checkbox from '@/ui/checkbox';
import Link from 'next/link';
import api from '@/utils/api';
import useAuthStore from '@/store/auth';
import useModalStore from '@/store/modalStore';
import styles from './styles.module.css'

const SignUp = ({dict, showButton = true, renderModal = true}) => {
    const {create_account_modal} = dict.modal
    const {log_in, sign_up} = dict.button
    const { login, logout, hydrate } = useAuthStore();
    const { openSignUpModal, closeSignUpModal , isSignUpModalOpen , openLoginModal} = useModalStore();
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
    const [errorMessage, setErrorMessage] = useState('')
    const isLoginDisabled = name.trim() === '' || email.trim() === '' || password.trim() === '' || !isCheckboxChecked

    const handleCheckboxChange = (event) => {
        setIsCheckboxChecked(event.target.checked);
    };

    useEffect(() => {
        hydrate();
    }, [hydrate]);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword)
    }

    const handleName = (event) => {
        setName(event.target.value);
    }
    
    const handleEmail = (event) => {
            setEmail(event.target.value)
    }

    const handlePassword = (event) => {
            setPassword(event.target.value)
    }

    async function createUser () {
        if (!isCheckboxChecked) {
            console.error('You must agree to the privacy policy to create an account.');
            return;
        }

        const credentials = {
            email: email,
            name: name,
            password: password
        };

        try {
            // Debug: print axios baseURL used in the browser (helps detect cached/old bundles)
            if (typeof window !== 'undefined' && api && api.defaults) {
                console.warn('🔍 createUser() - axios baseURL =', api.defaults.baseURL);
            }

            const response = await api.post('/api/auth/sign_up/', credentials, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (response.status === 200 || response.status === 201) {
                console.log('Sign up successful', response.data);
                try {
                    login(response.data, true);
                    closeSignUpModal();
                } catch (loginError) {
                    console.error('Error during login 2222:', loginError);
                    setErrorMessage('Registration successful, but login failed. Please try logging in manually.');
                }
            } else {
                console.error('Login failed');
                logout();
            }
        } catch (error) {
            console.error('Error during login:', error);
            
            // Безпечна обробка помилок
            const errors = create_account_modal.errors || {};
            let errorMessage = errors.generic || 'An error occurred. Please try again.';
            
            if (error.response) {
                // Є відповідь від сервера
                const data = error.response.data;
                
                if (data) {
                    // Перевіряємо різні формати помилок
                    if (data.detail) {
                        if (Array.isArray(data.detail) && data.detail.length > 0) {
                            const firstError = data.detail[0];
                            if (firstError.ctx && firstError.ctx.reason) {
                                errorMessage = firstError.ctx.reason;
                            } else if (firstError.msg) {
                                errorMessage = firstError.msg;
                            } else if (typeof firstError === 'string') {
                                errorMessage = firstError;
                            }
                        } else if (typeof data.detail === 'string') {
                            errorMessage = data.detail;
                        }
                    } else if (data.message) {
                        errorMessage = data.message;
                    } else if (data.error) {
                        errorMessage = data.error;
                    }
                }
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setErrorMessage(errors[errorMessage] || errorMessage);
        }
    }
        
    const closeModalAndCreateUser = () => {
            if (!isLoginDisabled) {
                createUser().catch(error => console.error('Error fetching categories:', error))
            }
        }

    const handleOverlayClick = (event) => {
        if (event.target.id === "modal-overlay") {
            closeSignUpModal();
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === "Escape") {
            closeSignUpModal();
        }
    };

    useEffect(() => {
        if (!renderModal) {
            return;
        }

        if (isSignUpModalOpen) {
            document.addEventListener("mousedown", handleOverlayClick);
            document.addEventListener("keydown", handleKeyDown);
        }
        return () => {
            document.removeEventListener("mousedown", handleOverlayClick);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isSignUpModalOpen, renderModal]);

    return ( <div>
        {showButton && <Button onClick={openSignUpModal} title={sign_up} type='sign-up' />}
        {renderModal && (
            <Modal
                isOpen={isSignUpModalOpen}
                onClose={closeSignUpModal}
                className={styles.signup}
                type={'create-user'}
            >
                <div className={styles.singup__content}>
                    <h1 className={styles.signup__title}>{create_account_modal.create_account}</h1>
                    <div className={styles.signup__input_wrapper}>
                        <Input
                            id="name"
                            name="name"
                            type="name"
                            placeholder={create_account_modal.your_name}
                            value={name}
                            onChange={handleName}
                            required={true}
                        />
                        <Input
                            id="email"
                            name="email"
                            type="email"
                            placeholder={create_account_modal.e_mail}
                            value={email}
                            onChange={handleEmail}
                            required={true}
                        />
                        <Input
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            placeholder={create_account_modal.your_password}
                            value={password}
                            onChange={handlePassword}
                            showPassword={showPassword}
                            onShowPassword={togglePasswordVisibility}
                            required={true}
                        />
                    </div>

                    <div className={styles.signup__checkbox__wrapper}>
                        <div className={styles.signup__checkbox_block}>
                            <Checkbox type={"grey"} checked={isCheckboxChecked} onChange={handleCheckboxChange} />
                            <p className={styles.signup__checkbox__description}>
                                {create_account_modal.consent_to}
                                <Link className={styles.signup__checkbox__link} href={'#'}>{' '}{create_account_modal.privacy_policy}</Link>
                            </p>
                        </div>
                    </div>
                    <div className={styles.signup__button_wrapper}>
                        <Button onClick={closeModalAndCreateUser} title={sign_up} type="login-modal" disabled={isLoginDisabled} />
                        <Button onClick={openLoginModal} title={log_in} type="signup-modal"/>
                    </div>
                    {
                        errorMessage && <p className={styles.error_message}>{errorMessage}</p>
                    }
                    <p className={styles.signup__notification}>{create_account_modal.no_unsolicited}</p>
                </div>
            </Modal>
        )}
    </div>
    )
}

export default SignUp
